import { useState, useEffect, useId, useMemo } from "react";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { Badge } from "@/shared/ui/badge";
import { ChevronDown, X, AlertCircle, Check, Loader2 } from "lucide-react";
import type { Category } from "@/services/api/categories";
import { getCategoryLabel } from "@/features/categories/utils/categoryUtils";
import { useOnboardUser, usersKeys } from "../../queries";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

function parseEmails(raw: string): string[] {
  return raw
    .split(/[,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
}

const emailSchema = z.email();
function isValidEmail(value: string): boolean {
  return emailSchema.safeParse(value).success;
}

interface AddUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: Category[];
}

type Step = "input" | "confirm";
type EmailStatus = "pending" | "loading" | "success" | "error";
type EmailResult = { status: EmailStatus; error?: string };

export function AddUserDialog({
  open,
  onOpenChange,
  categories,
}: AddUserDialogProps) {
  const [emailInput, setEmailInput] = useState("");
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [step, setStep] = useState<Step>("input");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);
  const [emailStatuses, setEmailStatuses] = useState<Record<string, EmailResult>>({});
  const { mutateAsync: onboardUser } = useOnboardUser();
  const queryClient = useQueryClient();

  const parsedEmails = useMemo(() => parseEmails(emailInput), [emailInput]);
  const invalidEmails = useMemo(
    () => parsedEmails.filter((e) => !isValidEmail(e)),
    [parsedEmails],
  );
  const validEmails = useMemo(
    () => parsedEmails.filter((e) => isValidEmail(e)),
    [parsedEmails],
  );
  const duplicates = useMemo(() => {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    for (const e of validEmails) {
      if (seen.has(e)) dupes.add(e);
      seen.add(e);
    }
    return dupes;
  }, [validEmails]);
  const uniqueValidEmails = useMemo(
    () => [...new Set(validEmails)],
    [validEmails],
  );

  useEffect(() => {
    if (!open) {
      const hadSubmissions = Object.values(emailStatuses).some(
        (r) => r.status === "success",
      );
      if (hadSubmissions) {
        queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      }
      setEmailInput("");
      setSelectedCategoryIds([]);
      setStep("input");
      setIsSubmitting(false);
      setTouched(false);
      setEmailStatuses({});
    }
  }, [open]);

  const handleReview = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (uniqueValidEmails.length === 0) return;
    if (invalidEmails.length > 0) return;
    setStep("confirm");
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    const initStatuses: Record<string, EmailResult> = {};
    for (const email of uniqueValidEmails) {
      initStatuses[email] = { status: "pending" };
    }
    setEmailStatuses(initStatuses);

    for (const email of uniqueValidEmails) {
      setEmailStatuses((prev) => ({
        ...prev,
        [email]: { status: "loading" },
      }));
      try {
        await onboardUser({ email, categoryIds: selectedCategoryIds });
        setEmailStatuses((prev) => ({
          ...prev,
          [email]: { status: "success" },
        }));
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Unknown error";
        setEmailStatuses((prev) => ({
          ...prev,
          [email]: { status: "error", error: message },
        }));
      }
    }

    setIsSubmitting(false);
  };

  const doneCount = Object.values(emailStatuses).filter(
    (r) => r.status === "success" || r.status === "error",
  ).length;
  const hasFinished = doneCount === uniqueValidEmails.length && doneCount > 0;
  const hasErrors = Object.values(emailStatuses).some(
    (r) => r.status === "error",
  );

  const toggleCategory = (categoryId: number) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId],
    );
  };

  const removeCategory = (categoryId: number) => {
    setSelectedCategoryIds((prev) => prev.filter((id) => id !== categoryId));
  };

  const selectedCategories = categories.filter((cat) =>
    selectedCategoryIds.includes(Number(cat.id)),
  );
  const descriptionId = useId();

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v && step === "confirm") {
          setStep("input");
          return;
        }
        onOpenChange(v);
      }}
    >
      <DialogContent
        className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto"
        aria-describedby={descriptionId}
      >
        {step === "input" ? (
          <>
            <DialogHeader>
              <DialogTitle>Add Users</DialogTitle>
              <DialogDescription>
                Enter one or more email addresses separated by commas or
                semicolons, then select categories.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleReview} className="space-y-4">
              <div className="space-y-2">
                <label
                  htmlFor="emails"
                  className="text-sm font-medium text-ink-900"
                >
                  Email addresses
                </label>
                <textarea
                  id="emails"
                  className="flex min-h-[80px] w-full rounded-md border border-grid-200 bg-white px-3 py-2 text-sm placeholder:text-ink-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
                  placeholder="user1@example.com, user2@example.com; user3@example.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onBlur={() => setTouched(true)}
                  disabled={isSubmitting}
                />
                {parsedEmails.length > 0 && (
                  <p className="text-xs text-ink-500">
                    {uniqueValidEmails.length} valid email
                    {uniqueValidEmails.length !== 1 ? "s" : ""} detected
                    {duplicates.size > 0 &&
                      ` (${duplicates.size} duplicate${duplicates.size !== 1 ? "s" : ""} removed)`}
                  </p>
                )}
                {touched && invalidEmails.length > 0 && (
                  <div className="flex items-start gap-1.5 text-xs text-red-600">
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>
                      Invalid:{" "}
                      {invalidEmails.map((e, i) => (
                        <span key={e}>
                          <code className="bg-red-50 px-1 rounded">{e}</code>
                          {i < invalidEmails.length - 1 ? ", " : ""}
                        </span>
                      ))}
                    </span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-ink-900">
                    Categories
                  </label>
                  <button
                    type="button"
                    className="text-xs text-accent-600 hover:text-accent-700 hover:underline disabled:opacity-50"
                    disabled={isSubmitting}
                    onClick={() => {
                      const allIds = categories.map((c) => Number(c.id));
                      setSelectedCategoryIds((prev) =>
                        prev.length === allIds.length ? [] : allIds,
                      );
                    }}
                  >
                    {selectedCategoryIds.length === categories.length
                      ? "Deselect all"
                      : "Select all"}
                  </button>
                </div>
                <div className="space-y-3">
                  {selectedCategories.length > 0 && (
                    <div className="flex flex-wrap gap-2 rounded-lg border border-grid-200 bg-grid-50 p-3 min-h-[60px]">
                      {selectedCategories.map((category) => (
                        <Badge
                          key={category.id}
                          variant="secondary"
                          className="flex items-center gap-1.5 pr-1"
                        >
                          {getCategoryLabel(category)}
                          <button
                            type="button"
                            onClick={() =>
                              removeCategory(Number(category.id))
                            }
                            className="ml-1 rounded-full hover:bg-grid-200 p-0.5 transition-colors"
                            disabled={isSubmitting}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-between"
                        disabled={isSubmitting}
                      >
                        <span>
                          {selectedCategories.length > 0
                            ? `Add more categories (${selectedCategories.length} selected)`
                            : "Select categories"}
                        </span>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="max-h-60 w-(--radix-dropdown-menu-trigger-width) overflow-y-auto">
                      {categories.map((category) => (
                        <DropdownMenuCheckboxItem
                          key={category.id}
                          checked={selectedCategoryIds.includes(
                            Number(category.id),
                          )}
                          onCheckedChange={() =>
                            toggleCategory(Number(category.id))
                          }
                        >
                          {getCategoryLabel(category)}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={uniqueValidEmails.length === 0}
                >
                  Review &amp; Confirm
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                {hasFinished
                  ? hasErrors
                    ? "Onboarding Completed with Errors"
                    : "Onboarding Complete"
                  : "Confirm Onboarding"}
              </DialogTitle>
              <DialogDescription>
                {hasFinished ? (
                  hasErrors
                    ? "Some users could not be onboarded. See details below."
                    : "All users have been successfully onboarded."
                ) : (
                  <>
                    The following {uniqueValidEmails.length} user
                    {uniqueValidEmails.length !== 1 ? "s" : ""} will be
                    onboarded
                    {selectedCategories.length > 0 &&
                      ` with ${selectedCategories.length} categor${selectedCategories.length !== 1 ? "ies" : "y"}`}
                    .
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <p className="text-sm font-medium text-ink-900">
                  Email addresses
                  {isSubmitting &&
                    ` (${doneCount}/${uniqueValidEmails.length})`}
                </p>
                <ul className="max-h-[400px] overflow-y-auto rounded-lg border border-grid-200 bg-grid-50 divide-y divide-grid-200">
                  {uniqueValidEmails.map((email) => {
                    const result = emailStatuses[email];
                    return (
                      <li
                        key={email}
                        className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                      >
                        <span
                          className={`font-mono truncate ${result?.status === "error" ? "text-red-600" : "text-ink-700"}`}
                        >
                          {email}
                        </span>
                        {result?.status === "loading" && (
                          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-accent-500" />
                        )}
                        {result?.status === "success" && (
                          <Check className="h-4 w-4 shrink-0 text-green-600" />
                        )}
                        {result?.status === "error" && (
                          <span
                            className="flex items-center gap-1 shrink-0 text-xs text-red-600"
                            title={result.error}
                          >
                            <AlertCircle className="h-4 w-4" />
                            Failed
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {selectedCategories.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-sm font-medium text-ink-900">
                    Categories
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedCategories.map((category) => (
                      <Badge key={category.id} variant="secondary">
                        {getCategoryLabel(category)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              {hasFinished ? (
                <Button
                  type="button"
                  onClick={() => onOpenChange(false)}
                >
                  Close
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep("input")}
                    disabled={isSubmitting}
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    onClick={handleConfirm}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? "Onboarding..."
                      : `Onboard ${uniqueValidEmails.length} User${uniqueValidEmails.length !== 1 ? "s" : ""}`}
                  </Button>
                </>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
